import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Save, ClipboardList, Globe, Lock } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Switch, Card } from '../../../components/ui'

type Context = 'internal' | 'public'

interface Field {
  key: string
  label: string
  group: string
  locked: boolean
  visible: boolean
  required: boolean
}

const CONTEXT_META: Record<Context, { label: string; icon: typeof ClipboardList; desc: string }> = {
  internal: { label: 'Cadastro interno', icon: ClipboardList, desc: 'Campos do modal de cadastro dentro da plataforma.' },
  public: { label: 'Link público (rede)', icon: Globe, desc: 'Campos do formulário público da rede de cada liderança.' },
}

export default function VoterFormConfigPage() {
  const navigate = useNavigate()
  const [context, setContext] = useState<Context>('internal')
  const [fields, setFields] = useState<Field[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback((ctx: Context) => {
    setLoading(true)
    axios.get(buildApiUrl('/v1/settings/voter-form'), { params: { context: ctx } })
      .then(res => setFields(res.data.data.fields))
      .catch(() => setFields([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load(context) }, [context, load])

  function setVisible(key: string, value: boolean) {
    setFields(fs => fs.map(f => f.key === key
      ? { ...f, visible: value, required: value ? f.required : false } // ocultar tira o obrigatório
      : f))
  }
  function setRequired(key: string, value: boolean) {
    setFields(fs => fs.map(f => f.key === key
      ? { ...f, required: value, visible: value ? true : f.visible } // obrigatório implica aparecer
      : f))
  }

  async function save() {
    if (saving) return
    setSaving(true)
    try {
      await axios.put(buildApiUrl('/v1/settings/voter-form'), {
        fields: fields.map(f => ({ key: f.key, visible: f.visible, required: f.required })),
      }, { params: { context } })
      toast('success', 'Configuração salva')
    } catch { toast('error', 'Erro ao salvar') } finally { setSaving(false) }
  }

  const groups = Array.from(new Set(fields.map(f => f.group)))

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings/general')} className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Formulário de Eleitor</h1>
            <p className="text-gray-500 mt-0.5 text-sm">Escolha quais campos aparecem e quais são obrigatórios no cadastro.</p>
          </div>
        </div>
        <Button icon={<Save className="w-4 h-4" />} onClick={save} loading={saving} disabled={loading}>Salvar</Button>
      </div>

      {/* Abas de contexto */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {(Object.keys(CONTEXT_META) as Context[]).map(ctx => {
          const Icon = CONTEXT_META[ctx].icon
          return (
            <button key={ctx} onClick={() => setContext(ctx)}
              className={clsx('flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors', context === ctx ? 'bg-white text-brand-600 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
              <Icon className="w-4 h-4" /> {CONTEXT_META[ctx].label}
            </button>
          )
        })}
      </div>
      <p className="text-sm text-gray-500 -mt-3">{CONTEXT_META[context].desc}</p>

      {loading ? (
        <Card><div className="py-10 text-center text-sm text-gray-400">Carregando…</div></Card>
      ) : (
        <div className="space-y-5">
          {groups.map(group => (
            <Card key={group} padding="none">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{group}</h3>
                <div className="flex items-center gap-10 pr-1">
                  <span className="text-xs font-medium text-gray-400 w-16 text-center">Aparece</span>
                  <span className="text-xs font-medium text-gray-400 w-20 text-center">Obrigatório</span>
                </div>
              </div>
              <div className="divide-y divide-gray-50">
                {fields.filter(f => f.group === group).map(f => (
                  <div key={f.key} className="flex items-center justify-between px-5 py-3.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-gray-800">{f.label}</span>
                      {f.locked && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                          <Lock className="w-3 h-3" /> sempre
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-10 pr-1 flex-shrink-0">
                      <div className="w-16 flex justify-center">
                        <Switch checked={f.visible} disabled={f.locked} onChange={v => setVisible(f.key, v)} />
                      </div>
                      <div className="w-20 flex justify-center">
                        <Switch checked={f.required} disabled={f.locked || !f.visible} onChange={v => setRequired(f.key, v)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
